import React, { useEffect, useState } from 'react';
import { Users as UsersIcon, Mail, Calendar, ShoppingCart, Phone, Search, Download } from 'lucide-react';
import '../../styles/admin/Users.css';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPurchases, setUserPurchases] = useState([]);
  const [showPurchases, setShowPurchases] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.users) {
          setUsers(data.users);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const name = (user.full_name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const query = searchTerm.trim().toLowerCase();

    if (!query) return true;
    return name.includes(query) || email.includes(query);
  });

  const exportUsersToCsv = () => {
    const rows = filteredUsers.map((user) => ({
      name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
    }));

    const headers = ['Name', 'Email', 'Phone'];
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csvLines = [
      headers.join(','),
      ...rows.map((row) => [row.name, row.email, row.phone].map(escapeCsv).join(',')),
    ];

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `users-export-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const viewPurchases = async (user) => {
    setSelectedUser(user);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}/purchases`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.purchases) {
          setUserPurchases(data.purchases);
        }
        setShowPurchases(true);
      }
    } catch (error) {
      console.error('Error fetching user purchases:', error);
    }
  };

  return (
    <div className="admin-users">
      <div className="users-header">
        <div>
          <h1>User Management</h1>
          <p>View and manage registered users.</p>
        </div>
        <div className="users-stats">
          <div className="stat-badge">
            <UsersIcon className="h-4 w-4" />
            <span>{users.length} Users</span>
          </div>
        </div>
      </div>

      <div className="users-table-card">
        <div className="users-toolbar">
          <div className="users-search-wrap">
            <Search className="h-4 w-4" />
            <input
              type="text"
              placeholder="Search by customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="users-search-input"
            />
          </div>
          <button className="btn-primary-sm" onClick={exportUsersToCsv}>
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>

        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Joined</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-10">Loading...</td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-10">No users found.</td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <span className="user-name">{user.full_name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="email-cell">
                      <Mail className="h-4 w-4" />
                      {user.email}
                    </div>
                  </td>
                  <td>
                    <div className="phone-cell">
                      <Phone className="h-4 w-4" />
                      {user.phone || '-'}
                    </div>
                  </td>
                  <td>
                    <div className="date-cell">
                      <Calendar className="h-4 w-4" />
                      {new Date(user.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="text-right">
                    <button
                      className="btn-outline-sm"
                      onClick={() => viewPurchases(user)}
                    >
                      <ShoppingCart className="h-4 w-4" />
                      View Purchases
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPurchases && selectedUser && (
        <div className="dialog-overlay" onClick={() => setShowPurchases(false)}>
          <div className="dialog-content dialog-lg" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Purchases - {selectedUser.full_name || selectedUser.email}</h2>
            </div>
            <div className="dialog-body">
              {userPurchases.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No purchases found.</p>
              ) : (
                <table className="purchases-table">
                  <thead>
                    <tr>
                      <th>Program</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPurchases.map((purchase, idx) => (
                      <tr key={idx}>
                        <td>{purchase.program_name || 'Unknown'}</td>
                        <td>{`\u20B9${purchase.amount_paid}`}</td>
                        <td>{new Date(purchase.purchase_date).toLocaleDateString()}</td>
                        <td>
                          <span className={`badge ${purchase.status === 'completed' ? 'badge-success' : 'badge-pending'}`}>
                            {purchase.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setShowPurchases(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
